import React, { useState, useEffect } from "react";
import Button from "../../components/common/button";
import TicketNo from "../../components/ticket/ticketNo";
import TicketList from "../../components/ticket/ticketList";
import { FaFilter } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllLuckyTickets,
  fetchLuckyTicketsByUserAndTicketNo,
  fetchLuckyTicketsByDate,
  fetchLuckyTicketsByDateAndTicketNo,
} from "../../redux/slices/luckyNumberSlice";
import { useTranslation } from "react-i18next";
import BackdropSpinner from "../../components/backdropSpinner";

const TicketDetails = () => {
  const [selectedTicketNo, setSelectedTicketNo] = useState(null);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [errors, setErrors] = useState("");
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Fetch ticket data based on state
  const { luckyNumbers, loading, error } = useSelector(
    (state) => state.luckyNumbers
  );
  const userID = localStorage.getItem("userID");

  useEffect(() => {
    // Fetch all tickets initially
    dispatch(fetchAllLuckyTickets());
  }, [dispatch, userID]);

  useEffect(() => {
    if (selectedTicketNo != null) {
      dispatch(
        fetchLuckyTicketsByUserAndTicketNo({
          userId: userID,
          ticketNo: selectedTicketNo,
        })
      );
    }
  }, [dispatch, selectedTicketNo]);

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
    const day = String(d.getDate()).padStart(2, "0"); // Ensure 2 digits
    return `${year}-${month}-${day}`;
  };

  const handleFilterTickets = () => {
    const today = new Date();
    const start_date = new Date(fromDate);
    const end_date = new Date(toDate);

    // Validate if dates are in the future
    if (fromDate && start_date > today) {
      setErrors(t("ticketDetails.errors.fromDateFuture"));
      return;
    }
    if (toDate && end_date > today) {
      setErrors(t("ticketDetails.errors.toDateFuture"));
      return;
    }

    // Validate if "To" date is before "From" date
    if (fromDate && toDate && start_date > end_date) {
      setErrors(t("ticketDetails.errors.toDateBeforeFromDate"));
      return;
    }

    // Clear error and proceed
    setErrors("");
    if (fromDate && toDate && selectedTicketNo) {
      dispatch(
        fetchLuckyTicketsByDateAndTicketNo({
          start_date: formatDate(fromDate),
          end_date: formatDate(toDate),
          ticketNo: selectedTicketNo,
        })
      );
    } else if (fromDate && toDate) {
      dispatch(
        fetchLuckyTicketsByDate({
          start_date: formatDate(fromDate),
          end_date: formatDate(toDate),
        })
      );
    }
  };

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setSelectedTicketNo(null);
    setShowDateFilter(false);
    setErrors("");
    dispatch(fetchAllLuckyTickets());
  };

  const toggleDateFilter = () => {
    setShowDateFilter(!showDateFilter);
  };

  return (
    <>
      {/* Title */}
      <div className="flex justify-center my-2">
        <p className="text-white text-lg sm:text-xl md:text-2xl font-medium text-center">
          {t("ticketDetails.headerTitle")}
        </p>
      </div>

      <div className="flex flex-col items-center justify-center px-4 py-4">
        {/* TicketNo component */}
        <TicketNo onSelectTicket={setSelectedTicketNo} />

        {/* Filter and ticket list container */}
        <div className="w-full max-w-4xl text-white rounded-lg shadow-lg p-6 mt-8">
          {/* Date filter header */}
          <div className="relative flex justify-between items-center mb-4">
            <span className="font-medium text-lg">
              {t("ticketDetails.selectTimePeriod")}
            </span>
            <button
              onClick={showDateFilter ? handleClearFilters : toggleDateFilter}
              className="flex items-center justify-center p-2 bg-gray-700 rounded hover:bg-gray-600"
            >
              {showDateFilter ? t("ticketDetails.clearFilters") : <FaFilter />}
            </button>
          </div>

          {/* Date filter inputs */}
          {showDateFilter && (
            <div className="flex flex-row justify-center items-center gap-2 lg:gap-4 mb-4">
              <input
                type="date"
                className="w-full lg:w-1/2 md:w-1/2 sm:w-1/3 p-2 rounded border border-gray-300 text-black"
                placeholder="From"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <input
                type="date"
                className="w-full lg:w-1/2 md:w-1/2 sm:w-1/3 p-2 rounded border border-gray-300 text-black"
                placeholder="To"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          )}

          {/* Display validation error */}
          {errors && <div className="text-red-500 mb-4">{errors}</div>}

          {/* Action button */}
          <div className="flex justify-center mb-4">
            <Button
              text={t("ticketDetails.showDetails")}
              onClick={handleFilterTickets}
            />
          </div>

          {/* Ticket results header */}
          <div className="flex items-center gap-4 py-4 border-b border-gray-600">
            <h1 className="text-xl font-serif font-medium">
              {t("ticketDetails.ticketResults")}
            </h1>
          </div>

          {/* Scrollable Ticket List */}
          <div className="h-[350px] overflow-y-scroll">
            {loading ? (
              <BackdropSpinner />
            ) : error ? (
              <div>Error: {error}</div>
            ) : (
              <TicketList ticketsData={luckyNumbers} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketDetails;
